<template>
    <div
        class="chat_info_skeleton"
        role="status"
        :aria-label="label"
    >
        <span class="chat_skeleton_sr_only">
            {{ label }}
        </span>

        <div
            class="chat_info_skeleton_header"
            aria-hidden="true"
        >
            <span
                class="
                    chat_skeleton_block
                    chat_info_skeleton_avatar
                "
            />

            <span class="chat_info_skeleton_identity">
                <span
                    class="
                        chat_skeleton_block
                        chat_info_skeleton_name
                    "
                />

                <span
                    class="
                        chat_skeleton_block
                        chat_info_skeleton_status
                    "
                />
            </span>

            <span class="chat_info_skeleton_actions">
                <span
                    v-for="index in actionCount"
                    :key="'chat-info-action-' + index"
                    class="
                        chat_skeleton_block
                        chat_info_skeleton_action
                    "
                />
            </span>
        </div>

        <div
            class="chat_info_skeleton_messages"
            aria-hidden="true"
        >
            <span
                v-for="index in bubbleCount"
                :key="'chat-info-message-' + index"
                class="
                    chat_skeleton_block
                    chat_info_skeleton_bubble
                "
                :class="{
                    'chat_info_skeleton_bubble--sent':
                        isSentBubble(index),
                    'chat_info_skeleton_bubble--short':
                        isShortBubble(index)
                }"
            />
        </div>

        <div
            class="chat_info_skeleton_composer"
            aria-hidden="true"
        >
            <span
                class="
                    chat_skeleton_block
                    chat_info_skeleton_input
                "
            />

            <span
                class="
                    chat_skeleton_block
                    chat_info_skeleton_send
                "
            />
        </div>
    </div>
</template>

<script>
export default {
    name: 'ChatInfoSkeleton',

    props: {
        actionCount: {
            type: Number,
            default: 3,

            validator(value) {
                return (
                    Number.isInteger(value) &&
                    value > 0 &&
                    value <= 6
                );
            }
        },

        bubbleCount: {
            type: Number,
            default: 7,

            validator(value) {
                return (
                    Number.isInteger(value) &&
                    value > 0 &&
                    value <= 12
                );
            }
        },

        label: {
            type: String,
            default: 'Loading chat'
        }
    },

    methods: {
        isSentBubble(index) {
            return [2, 5, 7].indexOf(index) !== -1;
        },

        isShortBubble(index) {
            return [3, 5].indexOf(index) !== -1;
        }
    }
};
</script>

<style lang="scss" scoped>
.chat_info_skeleton {
    position: relative;
    display: flex;
    min-width: 0;
    min-height: 0;
    overflow: hidden;
    color: #fff;
    background: rgba(8, 11, 13, 0.82);
    flex-direction: column;
}

.chat_skeleton_sr_only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

.chat_skeleton_block {
    display: block;
    background-image: linear-gradient(
        90deg,
        #20262b 20%,
        #343d44 38%,
        #20262b 62%
    );
    background-position: 100% 0;
    background-size: 400% 100%;
    animation:
        chat-info-skeleton-shimmer
        1.35s ease-in-out infinite;
}

.chat_info_skeleton_header {
    display: flex;
    height: 58px;
    padding: 8px 14px;
    flex: 0 0 58px;
    align-items: center;
    background: #111111;
}

.chat_info_skeleton_avatar {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    flex: 0 0 48px;
}

.chat_info_skeleton_identity {
    display: block;
    min-width: 0;
    padding-left: 12px;
    flex: 1 1 auto;
}

.chat_info_skeleton_name,
.chat_info_skeleton_status {
    border-radius: 5px;
}

.chat_info_skeleton_name {
    width: 145px;
    max-width: 65%;
    height: 14px;
    margin-bottom: 9px;
}

.chat_info_skeleton_status {
    width: 75px;
    max-width: 45%;
    height: 9px;
}

.chat_info_skeleton_actions {
    display: flex;
    margin-left: 12px;
    gap: 12px;
}

.chat_info_skeleton_action {
    width: 31px;
    height: 31px;
    border-radius: 50%;
}

.chat_info_skeleton_messages {
    display: flex;
    min-height: 0;
    padding: 25px 24px 95px;
    overflow: hidden;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 18px;
}

.chat_info_skeleton_bubble {
    width: 42%;
    max-width: 330px;
    height: 45px;
    border-radius: 10px;

    &--sent {
        align-self: flex-end;
    }

    &--short {
        width: 27%;
        height: 36px;
    }
}

.chat_info_skeleton_composer {
    position: absolute;
    right: 18px;
    bottom: 14px;
    left: 18px;
    display: flex;
    height: 52px;
    padding: 5px 30px;
    align-items: center;
    border: 2px solid rgba(255, 255, 255, 0.15);
    background: rgba(0, 0, 0, 0.72);
    border-radius: 35px;
    -webkit-box-shadow: 0 1px 8px -1px rgba(162, 169, 176, .6);
    box-shadow: 0 1px 8px -1px rgba(162, 169, 176, .6);
}

.chat_info_skeleton_input {
    height: 14px;
    border-radius: 7px;
    flex: 1 1 auto;
}

.chat_info_skeleton_send {
    width: 34px;
    height: 34px;
    margin-left: 14px;
    border-radius: 50%;
    flex: 0 0 34px;
}

@keyframes chat-info-skeleton-shimmer {
    from {
        background-position: 100% 0;
    }

    to {
        background-position: -100% 0;
    }
}

@media (prefers-reduced-motion: reduce) {
    .chat_skeleton_block {
        animation: none;
    }
}
</style>
